import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { Fragment } from 'react';
import { Package } from 'lucide-react';

import { ITEM_TYPE_META, soft, yourShareText } from '../../screens/Accounting/Accounting.shared';
import { groupByCurrency, scaleMax } from '../../lib/clinicMoney';

// The «به تفکیک …» breakdowns as mobile cards (DESIGN-CONCEPT-MOBILE v2 §5) — the
// desktop rail's two cards, re-laid-out for a single narrow column rather than a
// 320px sidebar. Answers the doctor's «کدام مطب چقدر؟» / «کدام خدمت چقدر؟» glance.

const Card = ({ title, sub, children, testId }) => (
  <div
    data-testid={testId}
    style={{
      background: 'white', borderRadius: '1rem',
      border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      padding: '1rem 1.125rem',
    }}
  >
    <div style={{ fontSize: '0.875rem', fontWeight: 800, color: C.textPrimary, marginBottom: '0.125rem' }}>{title}</div>
    <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginBottom: '0.875rem' }}>{sub}</div>
    {children}
  </div>
);

// Rows come one per (key, currency), IRR first. With more than one currency each
// currency's rows are headed by its name, and a clinic bar is scaled against the
// largest bar IN ITS CURRENCY — never across (no exchange rate exists to compare
// a dirham bar with a toman one).
const CurrencyHeading = ({ currency }) => {
  const { moneyUnit } = useTranslation();
  return (
    <div style={{ fontSize: '0.6875rem', fontWeight: 800, color: C.textSecondary }} data-testid={`accounting-currency-heading-${currency}`}>
      {moneyUnit(currency)}
    </div>
  );
};

const ClinicRows = ({ clinics }) => {
  const { t, formatMoney, formatNumber, moneyUnit } = useTranslation();
  const groups = groupByCurrency(clinics);
  const mixed = groups.length > 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
      {groups.map(({ currency, rows }) => {
        const max = scaleMax(rows);
        return (
          <Fragment key={currency}>
            {mixed && <CurrencyHeading currency={currency} />}
            {rows.map((c) => (
              <div key={c.clinicId || 'none'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.clinicName || (c.clinicId ? t('accounting.unknownClinic') : t('accounting.noClinic'))}
                  </span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.teal, flexShrink: 0 }}>
                    {formatMoney(c.doctorShareTotal, { currency })}
                    <span style={{ fontSize: '0.625rem', fontWeight: 600, color: C.textMuted, marginInlineStart: '0.125rem' }}>{moneyUnit(currency, 'short')}</span>
                  </span>
                </div>
                <div style={{ height: '0.375rem', background: C.bg, borderRadius: '0.25rem', overflow: 'hidden', marginBottom: '0.375rem' }}>
                  <div
                    style={{ height: '100%', background: C.teal, borderRadius: '0.25rem', width: `${(c.grossTotal / max) * 100}%`, transition: 'width 0.5s' }}
                    data-testid={`accounting-clinic-bar-${c.clinicId || 'none'}-${currency}`}
                  />
                </div>
                <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                  {formatNumber(c.visitCount)} {t('patient.visit')}
                  {c.medicalServiceCount > 0 && ` · ${t('accounting.paraclinicalCount', { count: c.medicalServiceCount })}`}
                  {c.productCount > 0 && ` · ${t('accounting.goodsCount', { count: c.productCount })}`}
                </div>
              </div>
            ))}
          </Fragment>
        );
      })}
    </div>
  );
};

const TypeRows = ({ types }) => {
  const { t, formatMoney, formatNumber, moneyUnit } = useTranslation();
  const groups = groupByCurrency(types);
  const mixed = groups.length > 1;
  return (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
    {groups.map(({ currency, rows }) => (
      <Fragment key={currency}>
        {mixed && <CurrencyHeading currency={currency} />}
        {rows.map((line) => {
          // An itemType the accounting service has not typed falls through to its
          // raw code rather than blanking the row.
          const m = ITEM_TYPE_META(t)[line.itemType] || { label: line.itemType, Icon: Package, color: C.textSecondary };
          const Icon = m.Icon;
          return (
            <div key={line.itemType} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span style={{
                width: '2.125rem', height: '2.125rem', borderRadius: '0.625rem', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: soft(m.color, 0.1), color: m.color,
              }}>
                <Icon size={16} aria-hidden />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>{m.label}</div>
                <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>{formatNumber(line.count)} {t('common.units.item')}</div>
              </span>
              <span style={{ textAlign: 'start', flexShrink: 0 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: m.color }}>
                  {formatMoney(line.grossTotal, { currency })}
                  <span style={{ fontSize: '0.625rem', fontWeight: 600, color: C.textMuted, marginInlineStart: '0.125rem' }}>{moneyUnit(currency, 'short')}</span>
                </div>
                <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                  {line.doctorShareTotal != null ? yourShareText(line.doctorShareTotal, currency, t, formatMoney, moneyUnit) : t('accounting.noSplitRule')}
                </div>
              </span>
            </div>
          );
        })}
      </Fragment>
    ))}
  </div>
  );
};

const MobileAccountingBreakdown = ({ clinics = [], types = [] }) => {
  const { t } = useTranslation();
  if (!clinics.length && !types.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }} data-testid="accounting-breakdown">
      {types.length > 0 && (
        <Card title={t('accounting.byServiceType')} sub={t('accounting.totalForRange')} testId="accounting-breakdown-type">
          <TypeRows types={types} />
        </Card>
      )}
      {clinics.length > 0 && (
        <Card title={t('accounting.byClinic')} sub={t('accounting.shareByClinic')} testId="accounting-breakdown-clinic">
          <ClinicRows clinics={clinics} />
        </Card>
      )}
    </div>
  );
};

export default MobileAccountingBreakdown;
